import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, ChevronLeft, ChevronRight } from "lucide-react";
import { accentText } from "@/lib/text";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const mediaUrl = (p) => (p ? (p.startsWith("http") ? p : `${API}/files/${p}`) : "");

const FALLBACK = [
  { title: "A Birthday Anthem for Priya", category: "Birthday", poster: "https://images.unsplash.com/photo-1544563422-a46c75a351e5?crop=entropy&cs=srgb&fm=jpg&q=85", video_path: "", audio_path: "" },
  { title: "Their First Dance, Their Own Song", category: "Wedding", poster: "https://images.pexels.com/photos/10360898/pexels-photo-10360898.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", video_path: "", audio_path: "" },
  { title: "She Said Yes Before the Chorus", category: "Proposal", poster: "https://images.unsplash.com/photo-1714647211960-540efeff3c90?crop=entropy&cs=srgb&fm=jpg&q=85", video_path: "", audio_path: "" },
];

export default function SampleCarousel({ content }) {
  const slides = content?.carousel?.length ? content.carousel : FALLBACK;
  const scrollRef = useRef(null);
  const audioRefs = useRef([]);
  const [playingIdx, setPlayingIdx] = useState(-1);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelector("[data-card]");
    const w = card ? card.offsetWidth + 20 : 320;
    el.scrollBy({ left: dir * w * 2, behavior: "smooth" });
  };

  const toggle = (i) => {
    const audio = audioRefs.current[i];
    if (!audio) return;
    if (playingIdx === i) {
      audio.pause();
      setPlayingIdx(-1);
      return;
    }
    audioRefs.current.forEach((a, j) => { if (a && j !== i) { a.pause(); a.currentTime = 0; } });
    audio.currentTime = 0;
    audio.play().then(() => setPlayingIdx(i)).catch(() => setPlayingIdx(-1));
  };

  return (
    <section className="pt-10 sm:pt-14 pb-16 sm:pb-24 overflow-hidden" data-testid="sample-carousel-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }} className="flex items-end justify-between gap-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-pink font-semibold mb-4">{content?.carousel_eyebrow ?? "Watch & listen"}</p>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight leading-[1.1]">
              {accentText(content?.carousel_heading ?? "Real songs, ~real reactions~", "text-grape")}
            </h2>
          </div>
          <div className="hidden sm:flex gap-2.5">
            <button data-testid="carousel-prev-btn" onClick={() => scroll(-1)} aria-label="Scroll samples back"
              className="btn-pop w-11 h-11 rounded-full bg-white border-2 border-ink flex items-center justify-center shadow-[3px_3px_0_#2D1A32]">
              <ChevronLeft size={18} />
            </button>
            <button data-testid="carousel-next-btn" onClick={() => scroll(1)} aria-label="Scroll samples forward"
              className="btn-pop w-11 h-11 rounded-full bg-white border-2 border-ink flex items-center justify-center shadow-[3px_3px_0_#2D1A32]">
              <ChevronRight size={18} />
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          ref={scrollRef}
          className="mt-10 flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          data-testid="sample-carousel"
        >
          {slides.map((slide, i) => {
            const playing = playingIdx === i;
            return (
              <div
                key={i}
                data-card
                data-testid={`carousel-card-${i}`}
                className="snap-start shrink-0 w-[78%] sm:w-[46%] lg:w-[calc(25%-15px)] rounded-3xl bg-white border-[3px] border-ink shadow-[6px_6px_0_#2D1A32] overflow-hidden"
              >
                <div className="relative h-72 bg-ink">
                  {slide.video_path ? (
                    <video
                      src={mediaUrl(slide.video_path)}
                      poster={mediaUrl(slide.poster)}
                      className="w-full h-full object-cover"
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="metadata"
                    />
                  ) : (
                    <img src={mediaUrl(slide.poster)} alt={slide.title} className="w-full h-full object-cover" loading="lazy" />
                  )}
                  {slide.audio_path && (
                    <audio ref={(el) => (audioRefs.current[i] = el)} src={mediaUrl(slide.audio_path)} onEnded={() => setPlayingIdx(-1)} preload="none" />
                  )}

                  <span className="sticker absolute top-3 left-3 rounded-full bg-pink text-white border-2 border-ink px-3 py-1 font-display font-semibold text-[11px] rotate-[-4deg] z-10" data-testid={`carousel-badge-${i}`}>
                    {slide.category}
                  </span>

                  {slide.audio_path && (
                    <button
                      data-testid={`carousel-play-btn-${i}`}
                      onClick={() => toggle(i)}
                      aria-label={playing ? "Pause song" : "Play song"}
                      className={`btn-pop absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 rounded-full border-2 border-ink px-3.5 py-1.5 font-display font-semibold text-[11px] shadow-[2px_2px_0_#2D1A32] ${playing ? "bg-mint text-ink rotate-[3deg]" : "bg-white text-ink"}`}
                    >
                      {playing ? <Pause size={12} fill="currentColor" /> : <Play size={12} fill="currentColor" />}
                      {playing ? "Tap to pause" : "Tap to play"}
                    </button>
                  )}

                  {playing && (
                    <div className="absolute bottom-3 left-0 right-0 flex items-end justify-center gap-1 h-6 z-10" aria-hidden="true">
                      {[...Array(12)].map((_, j) => (
                        <span key={j} className="w-1 rounded-full bg-white/90 eq-bar"
                          style={{ height: `${30 + 60 * Math.abs(Math.sin(j * 1.3))}%`, animationDelay: `${j * 0.08}s` }} />
                      ))}
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <p className="font-display font-semibold text-base leading-tight" data-testid={`carousel-title-${i}`}>{slide.title}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-lilac mt-1">
                    {slide.audio_path ? "Tap to hear the song" : "Sample media coming soon"}
                  </p>
                </div>
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
